import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { EditLegendContainer } from '../../Legend';
import { LegendItem } from '../../../components/Legend';
import { reqLegend, toggleEditLegend } from '../../../actions/legendActions';
import { trackEvent } from '../../../actions/analyticsActions';
import { getLayerUnits } from '../../../config/legendUnits';
import { IN_SEASON } from '../../../constants/mapLayerIds';
import styles from '../harvestAndPlanting/HarvestAndPlantingLegend.css';
import { processIntervals } from '../../../utils/processUnits';
import { usernameSelector, userSelector } from '../../../selectors/userSelectors';
import { AppState } from '../../../store/types/Store';
import { Interval, EventDates, User } from '../../../types';

const LAYER = IN_SEASON.APPLICATIONS;

interface ApplicationLegendProps {
  crop: string;
  currentUserHasFetched: boolean;
  dates: EventDates;
  fieldUuid: string;
  index: number;
  intervals: Interval[];
  isFetching: boolean;
  locale: string;
  reqLegend: typeof reqLegend;
  season: string;
  testId: string;
  toggleEditLegend: typeof toggleEditLegend;
  trackAnalyticsEvent: (noun: string, verb: string, data: object) => unknown;
  units?: object;
  unitsLocaleGroup: string;
  username: string;
  user: Partial<User> & { uuid: string };
}

export class ApplicationLegend extends Component<ApplicationLegendProps> {
  componentDidMount() {
    const {
      username,
      fieldUuid,
      dates,
      crop,
      season,
      intervals,
      reqLegend: reqLegendProp,
      user,
    } = this.props;
    if (!intervals?.length) {
      const subLayer = `${dates.endDate}_${crop}`;
      reqLegendProp(username, fieldUuid, dates, crop, LAYER, season, subLayer, user.uuid);
    }
  }

  componentDidUpdate(prevProps: ApplicationLegendProps) {
    const {
      username,
      fieldUuid,
      dates,
      crop,
      season,
      reqLegend: reqLegendProp,
      user,
      intervals,
    } = this.props;
    if (
      !intervals?.length &&
      (prevProps.crop !== crop ||
        prevProps.dates.endDate !== dates.endDate ||
        prevProps.season !== season)
    ) {
      const subLayer = `${dates.endDate}_${crop}`;
      reqLegendProp(username, fieldUuid, dates, crop, LAYER, season, subLayer, user.uuid);
    }
  }

  toggleEditLegend = () => {
    const {
      index,
      trackAnalyticsEvent,
      intervals,
      toggleEditLegend: toggleEditLegendProp,
    } = this.props;
    toggleEditLegendProp(index);

    trackAnalyticsEvent('edit_legend', 'clicked', {
      numberOfSteps: intervals?.length ?? 0,
      min: intervals?.[0] && (intervals[0] as Interval & { value?: number }).value,
      max:
        intervals?.[intervals.length - 1] &&
        (intervals[intervals.length - 1] as Interval & { value?: number }).value,
    });
  };

  render() {
    const {
      currentUserHasFetched,
      intervals,
      locale,
      testId,
      units,
      unitsLocaleGroup,
      dates,
      crop,
      index,
      isFetching,
    } = this.props;

    const subLayer = `${dates.endDate}_${crop}`;

    if (!currentUserHasFetched || !intervals?.length) {
      return <LegendItem isFetching />;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const finalIntervals = processIntervals({intervals, units, locale}) as any;
    return (
      <div
        className={styles.legendContainer}
        data-test-id={`${testId}-inSeason-${LAYER}`}
      >
        <EditLegendContainer
          crop={crop}
          dates={dates}
          index={index}
          intervals={finalIntervals.intervals}
          processedIntervals={finalIntervals}
          subLayer={subLayer}
          units={finalIntervals}
          unitsLocaleGroup={unitsLocaleGroup}
          year={dates.year}
        />
        <LegendItem
          intervals={finalIntervals.intervals}
          isEditable={Boolean(finalIntervals.key)}
          isFetching={isFetching}
          toggleEditLegend={this.toggleEditLegend}
          units={finalIntervals.key}
        />
      </div>
    );
  }
}

function mapStateToProps(state: AppState & { currentUser: { hasFetched: boolean } }, props: { index: number }) {
  const activeSeason = state.seasons.activeSeasons[props.index];
  const seasonEntry = state.seasons.list?.[activeSeason?.id];
  const liquidData = seasonEntry?.liquidData;
  const liquidLayer = state.layers.list?.[activeSeason?.id]?.[LAYER] as {
    activeSubLayerIds?: string[];
    subLayers?: Record<string, { legend?: Interval[]; localeGroup?: string }>;
  } | undefined;

  if (!liquidData || !liquidLayer) {
    return {
      crop: '',
      currentUserHasFetched: state.currentUser?.hasFetched ?? false,
      dates: { endDate: 0, startDate: 0, year: 0 },
      fieldUuid: state.field.uuid.toUpperCase(),
      intervals: [],
      isFetching: state.legend.isFetching,
      locale: state.app.locale,
      season: activeSeason?.id ?? '',
      units: {},
      unitsLocaleGroup: '',
      username: usernameSelector(state),
      user: userSelector(state),
    };
  }

  const activeSubLayerId = liquidLayer.activeSubLayerIds?.[props.index] ?? '';
  const splitIndex = activeSubLayerId.indexOf('_');
  const activeEndDate = parseInt(activeSubLayerId.slice(0, splitIndex), 10);
  const activeCrop = activeSubLayerId.slice(splitIndex + 1);
  const activeApp = liquidData.find(
    (application: { crop: string; liquidEndDate: number }) =>
      application.crop === activeCrop &&
      application.liquidEndDate === activeEndDate
  );

  if (!activeApp) {
    return {
      crop: '',
      currentUserHasFetched: state.currentUser?.hasFetched ?? false,
      dates: { endDate: 0, startDate: 0, year: 0 },
      fieldUuid: state.field.uuid.toUpperCase(),
      intervals: [],
      isFetching: state.legend.isFetching,
      locale: state.app.locale,
      season: activeSeason?.id ?? '',
      units: {},
      unitsLocaleGroup: '',
      username: usernameSelector(state),
      user: userSelector(state),
    };
  }

  const { crop, liquidEndDate, liquidStartDate, liquidYear } = activeApp;
  const dates = {
    endDate: liquidEndDate,
    startDate: liquidStartDate,
    year: liquidYear,
  };

  const subLayer = `${liquidEndDate}_${crop}`;
  const subLayerData = liquidLayer.subLayers?.[subLayer] ?? {};
  const { legend, localeGroup } = subLayerData;
  const units = getLayerUnits(LAYER, localeGroup);

  return {
    crop,
    currentUserHasFetched: state.currentUser?.hasFetched ?? false,
    dates,
    fieldUuid: state.field.uuid.toUpperCase(),
    intervals: legend ?? [],
    isFetching: state.legend.isFetching,
    locale: state.app.locale,
    season: activeSeason?.id ?? '',
    units,
    unitsLocaleGroup: localeGroup ?? '',
    username: usernameSelector(state),
    user: userSelector(state),
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
  return bindActionCreators(
    {
      reqLegend,
      toggleEditLegend,
      trackAnalyticsEvent: trackEvent,
    },
    dispatch
  ) as unknown as Pick<ApplicationLegendProps, 'reqLegend' | 'toggleEditLegend' | 'trackAnalyticsEvent'>;
}

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationLegend);
